import { createAppLogger } from "@bestowinc/logger/client";

import { getErrorTelemetryShellVersion } from "./context";
import { markFailureReported, wasFailureReported } from "./dedupe";
import type { ReportFailureArgs } from "./types";

const logger = createAppLogger({ name: "TINKERTOY-CLIENT:error-telemetry" });

export function reportTinkertoyFailure(args: ReportFailureArgs): void {
  try {
    const { message, error, shellVersion, ...telemetryFields } = args;
    const resolvedShellVersion = shellVersion ?? getErrorTelemetryShellVersion();

    logger.error(message, {
      error,
      ...telemetryFields,
      ...(resolvedShellVersion ? { shellVersion: resolvedShellVersion } : {}),
    });
  } catch {
    // Logger / transport failures must never break shell rendering.
  }
}

/**
 * Report a caught failure once. Marks {@link target} so downstream boundaries
 * skip duplicate emission.
 */
export function reportFailureOnce(
  target: object,
  args: ReportFailureArgs,
): void {
  if (wasFailureReported(target)) {
    return;
  }
  reportTinkertoyFailure(args);
  markFailureReported(target);
}

/** Report boundary activation unless the underlying failure was already reported. */
export function reportShellBoundaryFailure(
  routeError: unknown,
  args: Omit<ReportFailureArgs, "error" | "message" | "errorCode"> & {
    message: string;
    errorCode: string;
  },
): void {
  if (wasFailureReported(routeError)) {
    return;
  }

  const error =
    routeError instanceof Error ? routeError : new Error(args.message);

  reportFailureOnce(error, {
    ...args,
    error,
  });
}
